import { useState } from 'react';
import { ChevronRight, Home } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';

/** Folder breadcrumb derived from the S3 prefix (segments split on `/`). */
export function PrefixBar({
  prefix,
  onNavigate,
}: {
  prefix: string;
  onNavigate: (prefix: string) => void;
}) {
  const segments = prefix.split('/').filter(Boolean);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 'var(--vn-space-1)',
        marginBottom: 'var(--vn-space-3)',
        fontSize: 'var(--vn-font-size-sm)',
      }}
    >
      <button
        type="button"
        onClick={() => onNavigate('')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--vn-accent-text, var(--vn-accent-solid))', padding: 0, font: 'inherit' }}
      >
        <Home size={14} /> root
      </button>
      {segments.map((seg, i) => {
        const target = `${segments.slice(0, i + 1).join('/')}/`;
        const isLast = i === segments.length - 1;
        return (
          <span key={target} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
            <ChevronRight size={14} style={{ color: 'var(--vn-fg-muted)' }} />
            {isLast ? (
              <span style={{ color: 'var(--vn-fg-default)', fontWeight: 'var(--vn-font-weight-medium)' }}>{seg}</span>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate(target)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--vn-accent-text, var(--vn-accent-solid))', padding: 0, font: 'inherit' }}
              >
                {seg}
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

const SEG_RE = /^[^/]+$/;

/** New-folder dialog — creates a zero-byte marker at `prefix + name/`. */
export function NewFolderDialog({
  prefix,
  creating,
  onClose,
  onCreate,
}: {
  prefix: string;
  creating: boolean;
  onClose: () => void;
  onCreate: (fullPrefix: string) => void;
}) {
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const valid = SEG_RE.test(trimmed);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field
            label="Folder name"
            required
            error={name && !valid ? 'A folder name cannot contain "/".' : undefined}
          >
            {(p) => (
              <Input
                {...p}
                placeholder="e.g. images"
                value={name}
                invalid={!!name && !valid}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid || creating}
            onClick={() => onCreate(`${prefix}${trimmed}/`)}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
